import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AnimatePresence } from 'motion/react';
import { faAngleUp } from '@fortawesome/pro-solid-svg-icons/faAngleUp';
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons/faAngleDown';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';

import AddToPlaylistButton from '../../app/_components/playlist/AddToPlaylistButton';
import LibraryItemRow from './LibraryItemRow';
import {
  LibraryTopic,
  LibraryTopicGroup,
  LibraryTopicGroupPage,
} from '../../types/library';
import { usePagesContext } from '../../context/PagesProvider';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { H3, P } from '@osmosis/tailwind-ui/components/Text';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { text2, text3, text5 } from '@osmosis/tailwind-ui/styles';
import {
  useContentSize,
  useDeviceType,
} from '@osmosis/nextjs-components/context';
import MotionDiv from '@osmosis/nextjs-components/MotionDiv';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { Button } from '@osmosis/tailwind-ui/components/Button';

type Props = {
  topic: LibraryTopic;
  isTopicsOpen?: boolean;
};

type TopicBoxGroupProps = {
  group: LibraryTopicGroup;
  isDivider?: boolean;
};

/**
 * A library topic box.
 */
const LibraryTopicBox = (props: Props) => {
  const { topic, isTopicsOpen } = props;

  const { hash, title, pageCount, groups } = topic;
  const [isOpen, setIsOpen] = useState(true);
  const [isVisible, setIsVisible] = useState(true);

  const triggerId = `topic-box-trigger-${hash}`;
  const panelId = `topic-box-panel-${hash}`;

  const intl = useIntl();

  // Get first page in topic
  const [group] = topic.groups;
  const [firstPage] = group?.pages || [];

  const allPages = groups.reduce(
    (acc, g) => [...acc, ...g.pages],
    [] as LibraryTopicGroupPage[],
  );

  useEffect(() => {
    if (isTopicsOpen !== undefined) {
      setIsOpen(isTopicsOpen);
    }
  }, [isTopicsOpen]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    }
  }, [isOpen]);

  const handleAnimationComplete = useCallback(() => {
    if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen]);

  const { isMobile } = useDeviceType();
  const { getClassValue } = useContentSize();

  const { push } = usePagesContext();

  const containerResponsiveClasses = getClassValue({
    defaultClasses: 'lg:border-1',
    breakpoints: {
      lg: 'border-1',
    },
  });

  const actionsResponsiveClasses = getClassValue({
    defaultClasses: 'mr-1 md:mr-2 lg:mr-16',
    breakpoints: {
      md: 'mr-2',
      lg: 'mr-16',
      base: 'mr-1',
    },
  });

  const toggleTopics = () => setIsOpen((prev) => !prev);

  const handlePlayLink = (e: any) => {
    e.stopPropagation();
    push(firstPage.url);
  };

  const stopPropagation = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const iconClasses = `text-lg ${text3} transition duration-100 hover:bg-blue-700 hover:border-blue-700 hover:text-white [&_svg]:fill-neutral-500 [&:hover_svg]:fill-white`;

  const santizeHash = (path: string) => path.replace(/'/g, '');

  return (
    <Box
      data-testid="topic-box-container"
      id={santizeHash(hash)}
      className={cn(
        'mt-2 scroll-mt-[80px] rounded-[5px] border-0 border-solid border-neutral-300',
        containerResponsiveClasses,
      )}
    >
      <Flex
        data-testid="topic-box-toggle-topics"
        onClick={toggleTopics}
        className={`cursor-pointer items-center justify-between p-4 ${
          isOpen ? 'border-b-style-solid border-b border-neutral-300' : ''
        }`}
      >
        <H3
          data-testid="topic-box-title"
          className={`${text5} max-w-[55%] !text-xl`}
        >
          {title}
        </H3>
        <Flex>
          <Flex className={cn('items-center', actionsResponsiveClasses)}>
            {isOpen && (
              <>
                {firstPage && (
                  <Box className={`mr-2`}>
                    <IconButton
                      aria-label={intl.formatMessage({
                        id: 'library.topic_box.playButton',
                        defaultMessage: 'Play all',
                      })}
                      data-testid="topic-box-navigate-icon"
                      onClick={handlePlayLink}
                      variant="outline"
                      color="neutral"
                      className={iconClasses}
                      icon={
                        <Icon
                          style={{
                            fontSize: '16px',
                          }}
                          icon={faPlay}
                          className={`text-base`}
                        />
                      }
                    />
                  </Box>
                )}

                {!topic.parent.includes('To be retired') && (
                  <Box
                    data-testid="topic-box-add-to-playlist"
                    onClick={stopPropagation}
                    className={`mr-2 block`}
                  >
                    <AddToPlaylistButton
                      isIconButton={true}
                      data-testid="library-topic-box-add-to-playlist-button"
                      items={allPages.map((p) => ({
                        learnNodeID: p.learnNodeID,
                        name: p.title,
                      }))}
                      iconButtonProps={{
                        variant: 'outline',
                        color: 'neutral',
                        className: iconClasses,
                      }}
                    />
                  </Box>
                )}
              </>
            )}

            <P
              data-testid="topic-box-page-count"
              className={`${text5} text-sm`}
            >
              <FormattedMessage
                id="library.topic_box.topics_count"
                defaultMessage="{pageCount} topics"
                values={{ pageCount }}
              />
            </P>
          </Flex>
          <Button
            variant="transparent"
            id={triggerId}
            aria-label={
              isOpen
                ? intl.formatMessage(
                    {
                      id: 'library.topic_box.collapseButton',
                      defaultMessage: 'Collapse {topicTitle} topics',
                    },
                    {
                      topicTitle: title,
                    },
                  )
                : intl.formatMessage(
                    {
                      id: 'library.topic_box.expandButton',
                      defaultMessage: 'Expand {topicTitle} topics',
                    },
                    {
                      topicTitle: title,
                    },
                  )
            }
            data-testid="topic-box-toggle-icon"
            className={`items-center ${text5}`}
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              e.preventDefault();
              toggleTopics();
            }}
          >
            <Icon icon={isOpen ? faAngleUp : faAngleDown} size="1x" />
          </Button>
        </Flex>
      </Flex>
      <AnimatePresence initial={false}>
        {isOpen || !isMobile ? (
          <MotionDiv
            id={panelId}
            role="region"
            aria-labelledby={triggerId}
            data-testid="topic-box-groups-container"
            style={{
              overflow: 'hidden',
              display: !isOpen && !isVisible ? 'none' : undefined,
            }}
            initial={{ height: isOpen ? 0 : 'auto' }}
            animate={{ height: isOpen ? 'auto' : 0 }}
            exit={{ height: isOpen ? 'auto' : 0 }}
            transition={{ duration: 0.4 }}
            onAnimationComplete={handleAnimationComplete}
          >
            {groups.length &&
              groups.map((group, index) => (
                <LibraryTopicBoxGroup
                  key={group.hash}
                  group={group}
                  isDivider={index !== 0}
                />
              ))}
          </MotionDiv>
        ) : null}
      </AnimatePresence>
    </Box>
  );
};

const LibraryTopicBoxGroup = (props: TopicBoxGroupProps) => {
  const { hash, pages, title } = props.group;
  const { isDivider = false } = props;

  const refactoredHash = hash.split('-').join('_');

  const { getClassValue } = useContentSize();

  const libraryItemsContainerResponsiveClasses = getClassValue({
    defaultClasses: 'lg:flex-row',
    breakpoints: {
      lg: 'flex-row',
    },
  });

  return (
    <Box
      data-testid="topic-box-group-container"
      id={
        refactoredHash.endsWith('_')
          ? refactoredHash.slice(0, -1)
          : refactoredHash
      }
    >
      {isDivider && (
        <Box
          data-testid="topic-box-group-divider"
          className={`${text2} mx-[auto] mb-1 h-1 w-[90%]`}
        />
      )}

      <Box className={`mx-8 pl-4 pt-4`}>
        {title && (
          <P
            data-testid="topic-box-group-title"
            className={`${text5} !text-sm  font-bold`}
          >
            {title}
          </P>
        )}
      </Box>
      <Flex
        className={cn(
          'flex-col flex-wrap justify-between p-2',
          libraryItemsContainerResponsiveClasses,
        )}
      >
        <ul className="m-0 flex w-full list-none flex-wrap p-0">
          {pages.length &&
            pages.map((page) => (
              <LibraryItemRow key={page.learnNodeID} data={page} />
            ))}
        </ul>
      </Flex>
    </Box>
  );
};

export default LibraryTopicBox;
